import React, { useState, useEffect, useCallback } from 'react';
import {
  Card, Typography, Row, Col, Button, message, Spin,
  Layout, Select, Space, Tooltip, Avatar, Table, Tag
} from 'antd';
import {
  ReloadOutlined, ClockCircleOutlined, UserOutlined,
  LogoutOutlined, DollarOutlined, NodeIndexOutlined,
  FileTextOutlined, ImportOutlined, ExportOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import TabNavigation from './TabNavigation';
import BarChartComponent from './BarChartComponent';
import PieChartComponent from './PieChartComponent';
import SummaryMetricsCard from './SummaryMetricsCard';
import EnvVersionLabel from './Envversionlabel';
import AgentConfigService from '../services/AgentConfigService';

const { Text } = Typography;
const { Header, Sider, Content } = Layout;
const { Option } = Select;

// ── HCC Observatory service base URL ─────────────────────────────────────────
// Add REACT_APP_HCC_OBSERVATORY_URL=http://localhost:9039 to your .env file.
const HCC_OBS_BASE_URL = process.env.REACT_APP_HCC_OBSERVATORY_URL || 'http://localhost:9038';

// Rolling-window options (must match VALID_WINDOWS in hcc_observatory_service.py)
const WINDOW_OPTIONS = [
  { value: 7,  label: 'Last 7 Days'  },
  { value: 15, label: 'Last 15 Days' },
  { value: 30, label: 'Last 30 Days' },
  { value: 60, label: 'Last 60 Days' },
  { value: 90, label: 'Last 90 Days' },
];

// Custom color palette for all pie charts in this page (up to 15 slices).
// Extra colors beyond the number of slices are ignored by PieChartComponent.
const PIE_COLORS = [
  '#1a1040', // steel blue
  '#569fdb', // orange
  '#7185c6', // red
  '#76B7B2', // teal
  '#59A14F', // green
  '#075529', // yellow
  '#e54ab9', // lavender
  '#FF9DA7', // light pink
  '#9C755F', // brown
  '#332a26', // grey
  '#5b316e', // muted blue
  '#D62728', // brick red
  '#9467BD', // purple
  '#8C564B', // chestnut
  '#17BECF', // cyan
];

/**
 * HCCObservabilityHome
 * Observatory dashboard for the HCC coding pipeline.
 * Shows daily / batch token & cost trends, LLM model / chart-type /
 * processor cost pies, and a top-20 charts-by-cost table.
 */
const HCCObservabilityHome = ({
  username,
  sessionId,
  userRole,
  userFeatures,
  tabOrder,
  onLogout,
}) => {
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_API_BASE_URL || '';

  // ── Tab / feature state (mirrors AgentObservatoryHome) ───────────────────
  const [features,      setFeatures]      = useState(userFeatures || ['HUB', 'OBSERVATORY']);
  const [localTabOrder, setLocalTabOrder] = useState(tabOrder     || ['HUB', 'RCM', 'COLAB', 'MCP', 'OBSERVATORY']);

  // ── Filter state ──────────────────────────────────────────────────────────
  const [windowDays, setWindowDays] = useState(() => {
    return parseInt(localStorage.getItem('hcc_obs_windowDays') || '30', 10);
  });

  // ── Compute / last-computation state ─────────────────────────────────────
  const [computing,       setComputing]       = useState(false);
  const [lastComputation, setLastComputation] = useState(null);

  // ── Summary strip ─────────────────────────────────────────────────────────
  const [summaryData,    setSummaryData]    = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError,   setSummaryError]   = useState(null);

  // ── Per-chart loading flags ───────────────────────────────────────────────
  const [loadingStates, setLoadingStates] = useState({
    dailyTrend: false,
    batch:      false,
    chartType:  false,
    processor:  false,
    model:      false,
    topCharts:  false,
  });

  // ── Chart data ────────────────────────────────────────────────────────────
  const [dailyTrendData, setDailyTrendData] = useState(null);
  const [batchData,      setBatchData]      = useState(null);
  const [chartTypeData,  setChartTypeData]  = useState(null);
  const [processorData,  setProcessorData]  = useState(null);
  const [modelData,      setModelData]      = useState(null);
  const [topChartsData,  setTopChartsData]  = useState(null);

  // ── Authenticated fetch helper ────────────────────────────────────────────
  const fetchHcc = useCallback(async (path) => {
    const resp = await fetch(`${HCC_OBS_BASE_URL}${path}`, {
      headers: { Authorization: `Bearer ${sessionId}` },
      credentials: 'include',
    });
    if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
    const json = await resp.json();
    if (!json.success) throw new Error(json.message || 'API error');
    return json.data;
  }, [sessionId]);

  // ── Feature refresh (mirrors AgentObservatoryHome) ───────────────────────
  useEffect(() => {
    const refreshFeatures = async () => {
      try {
        const resp = await fetch(`${baseUrl}/features`, {
          headers: { Authorization: `Bearer ${sessionId}` },
          credentials: 'include',
        });
        if (resp.ok) {
          const { features: srv, tab_order } = await resp.json();
          setFeatures(srv);
          if (tab_order) setLocalTabOrder(tab_order);
        }
      } catch (err) {
        console.warn('Failed to refresh features:', err);
      }
    };
    if (sessionId && !userFeatures) refreshFeatures();
  }, [sessionId, userFeatures, baseUrl]);

  // ── Persist window selection ──────────────────────────────────────────────
  useEffect(() => {
    localStorage.setItem('hcc_obs_windowDays', String(windowDays));
  }, [windowDays]);

  // ── Auto-load on mount ────────────────────────────────────────────────────
  useEffect(() => {
    loadLastComputation();
    loadAllCharts(windowDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Number formatters (same as AgentObservatoryHome) ─────────────────────
  const formatNumber = (num) => {
    if (num == null) return '-';
    if (num >= 1_000_000) return (num / 1_000_000).toFixed(2) + 'M';
    if (num >= 1_000)     return (num / 1_000).toFixed(1) + 'K';
    return num.toLocaleString();
  };

  const formatCurrency = (num) => {
    if (num == null) return '-';
    if (num >= 1_000) return '$' + (num / 1_000).toFixed(2) + 'K';
    if (num >= 1)     return '$' + num.toFixed(2);
    return '$' + num.toFixed(4);
  };

  const formatLastComputation = () => {
    if (!lastComputation?.computed_at) return 'Never computed';
    const d    = new Date(lastComputation.computed_at);
    const diff = Math.floor((Date.now() - d) / 60000);
    if (diff < 1)  return 'Just now';
    if (diff < 60) return `${diff} minute${diff > 1 ? 's' : ''} ago`;
    const hrs = Math.floor(diff / 60);
    if (hrs < 24)  return `${hrs} hour${hrs > 1 ? 's' : ''} ago`;
    return d.toLocaleString();
  };

  // ── Summary metrics config for SummaryMetricsCard ────────────────────────
  const buildSummaryMetrics = (data) => {
    if (!data) return [];
    return [
      { key: 'input_tokens',  title: 'Input Tokens',  value: data.input_tokens,  formatter: formatNumber,   icon: <ImportOutlined />,     color: '#162349' },
      { key: 'output_tokens', title: 'Output Tokens', value: data.output_tokens, formatter: formatNumber,   icon: <ExportOutlined />,     color: '#1c97d4' },
      { key: 'input_cost',    title: 'Input Cost',    value: data.input_cost,    formatter: formatCurrency, icon: <DollarOutlined />,     color: '#162349' },
      { key: 'output_cost',   title: 'Output Cost',   value: data.output_cost,   formatter: formatCurrency, icon: <DollarOutlined />,     color: '#1c97d4' },
      { key: 'n_charts',      title: 'Charts',        value: data.n_charts,      formatter: formatNumber,   icon: <FileTextOutlined />,   color: '#349305' },
      { key: 'n_batches',     title: 'Batches',       value: data.n_batches,     formatter: formatNumber,   icon: <NodeIndexOutlined />,  color: '#ad5d07' },
    ];
  };

  // ── Generic single-chart loader ───────────────────────────────────────────
  const loadChart = async (key, setDataFn, fetchFn) => {
    try {
      setLoadingStates(prev => ({ ...prev, [key]: true }));
      const data = await fetchFn();
      setDataFn(data);
    } catch (err) {
      console.error(`Failed to load ${key}:`, err);
      setDataFn({ error: err.message });
    } finally {
      setLoadingStates(prev => ({ ...prev, [key]: false }));
    }
  };

  // ── Summary loader ────────────────────────────────────────────────────────
  const loadSummary = async (days) => {
    try {
      setSummaryLoading(true);
      setSummaryError(null);
      const data = await fetchHcc(`/api/hcc-observatory/summary?window_days=${days}`);
      setSummaryData(data);
    } catch (err) {
      console.error('Failed to load summary:', err);
      setSummaryError(err.message);
      setSummaryData(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  // ── Last-computation loader ───────────────────────────────────────────────
  const loadLastComputation = async () => {
    try {
      const data = await fetchHcc('/api/hcc-observatory/last-computation');
      setLastComputation(data);
    } catch (err) {
      console.error('Failed to load last computation:', err);
    }
  };

  // ── Load all charts + summary in parallel ─────────────────────────────────
  const loadAllCharts = (days) => {
    const d = days || windowDays;
    loadSummary(d);
    loadChart('dailyTrend', setDailyTrendData, () => fetchHcc(`/api/hcc-observatory/daily-trend?window_days=${d}`));
    loadChart('batch',      setBatchData,      () => fetchHcc(`/api/hcc-observatory/breakdown/batch?window_days=${d}`));
    loadChart('chartType',  setChartTypeData,  () => fetchHcc(`/api/hcc-observatory/breakdown/chart-type?window_days=${d}`));
    loadChart('processor',  setProcessorData,  () => fetchHcc(`/api/hcc-observatory/breakdown/processor?window_days=${d}`));
    loadChart('model',      setModelData,      () => fetchHcc(`/api/hcc-observatory/model-cost-share?window_days=${d}`));
    loadChart('topCharts',  setTopChartsData,  () => fetchHcc(`/api/hcc-observatory/top-charts?window_days=${d}&limit=20`));
  };

  // ── Apply filters ─────────────────────────────────────────────────────────
  const handleApplyFilters = () => loadAllCharts(windowDays);

  // ── Compute metrics ───────────────────────────────────────────────────────
  const handleComputeMetrics = async () => {
    try {
      setComputing(true);
      message.loading({ content: 'Computing HCC metrics…', key: 'hcc_compute', duration: 0 });

      const resp = await fetch(
        `${HCC_OBS_BASE_URL}/api/hcc-observatory/compute`,
        {
          method: 'POST',
          headers: { Authorization: `Bearer ${sessionId}` },
          credentials: 'include',
        }
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const json = await resp.json();
      if (!json.success) throw new Error('Compute returned success=false');

      await loadLastComputation();
      message.success({ content: 'Metrics computed successfully!', key: 'hcc_compute', duration: 3 });
      loadAllCharts(windowDays);
    } catch (err) {
      console.error('Compute failed:', err);
      message.error({ content: `Compute failed: ${err.message}`, key: 'hcc_compute', duration: 5 });
    } finally {
      setComputing(false);
    }
  };

  // ── Chart card wrapper (mirrors AgentObservatoryHome) ─────────────────────
  const renderChartCard = (title, chartComponent, loading, error) => (
    <Card
      title={<span style={{ fontSize: '16px', fontWeight: '600' }}>{title}</span>}
      style={{ minHeight: '500px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
      bodyStyle={{ padding: '16px' }}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
          <Spin size="large" tip={`Loading ${title}…`} />
        </div>
      ) : error ? (
        <div style={{ padding: '20px', textAlign: 'center', color: '#f5222d' }}>
          <div style={{ fontSize: '16px', marginBottom: '8px' }}>Error Loading {title}</div>
          <div style={{ fontSize: '14px' }}>{error}</div>
          <div style={{ marginTop: '12px', fontSize: '12px', color: '#999' }}>
            Click "Apply Filters" to retry
          </div>
        </div>
      ) : chartComponent ? (
        chartComponent
      ) : (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', color: '#999' }}>
          <div>
            <div style={{ fontSize: '16px', marginBottom: '8px' }}>No data available</div>
            <div style={{ fontSize: '14px' }}>Click "Apply Filters" to load data</div>
          </div>
        </div>
      )}
    </Card>
  );

  // ── Derive split chart objects from dual-metric API responses ─────────────
  // Each bar-chart row uses ONE API call but renders TWO BarChartComponents:
  //   tokens chart  →  { chartData, metrics: data.token_metrics, metadata }
  //   cost chart    →  { chartData, metrics: data.cost_metrics,  metadata }

  const dailyTokensChart = dailyTrendData && !dailyTrendData.error ? {
    chartData: dailyTrendData.chartData,
    metrics:   dailyTrendData.token_metrics,
    metadata:  { title: 'Daily Token Trend', subtitle: dailyTrendData.metadata?.subtitle },
  } : null;

  const dailyCostChart = dailyTrendData && !dailyTrendData.error ? {
    chartData: dailyTrendData.chartData,
    metrics:   dailyTrendData.cost_metrics,
    metadata:  { title: 'Daily Cost Trend', subtitle: dailyTrendData.metadata?.subtitle },
  } : null;

  const batchTokensChart = batchData && !batchData.error ? {
    chartData: batchData.chartData,
    metrics:   batchData.token_metrics,
    metadata:  { title: 'Batch Token Breakdown', subtitle: batchData.metadata?.subtitle },
  } : null;

  const batchCostChart = batchData && !batchData.error ? {
    chartData: batchData.chartData,
    metrics:   batchData.cost_metrics,
    metadata:  { title: 'Batch Cost Breakdown', subtitle: batchData.metadata?.subtitle },
  } : null;

  // ── Top-20 table columns ──────────────────────────────────────────────────
  const top20Columns = [
    {
      title: '#',
      key:   'rank',
      width: 32,
      render: (_, __, i) => (
        <span style={{ fontWeight: 600, color: '#8c8c8c', fontSize: '12px' }}>{i + 1}</span>
      ),
    },
    {
      title:     'Chart',
      dataIndex: 'chart_id',
      key:       'chart_id',
      ellipsis:  true,
      width:     110,
      render: (v) => (
        <Tooltip title={v}>
          <span style={{ fontFamily: 'monospace', fontSize: '11px' }}>{v}</span>
        </Tooltip>
      ),
    },
    {
      title:     'Type',
      dataIndex: 'chart_type',
      key:       'chart_type',
      width:     70,
      render: (v) => <Tag color="blue" style={{ fontSize: '10px' }}>{v}</Tag>,
    },
    {
      title:     'Enc',
      dataIndex: 'n_encounters',
      key:       'n_encounters',
      width:     42,
      align:     'right',
      render: (v) => <span style={{ fontSize: '12px' }}>{v}</span>,
    },
    {
      title:     'Steps',
      dataIndex: 'n_steps',
      key:       'n_steps',
      width:     46,
      align:     'right',
      render: (v) => <span style={{ fontSize: '12px' }}>{v}</span>,
    },
    {
      title:     'In $',
      dataIndex: 'input_cost',
      key:       'input_cost',
      width:     62,
      align:     'right',
      render: (v) => (
        <span style={{ fontSize: '12px', color: '#162349', fontWeight: 500 }}>
          ${v.toFixed(4)}
        </span>
      ),
    },
    {
      title:     'Out $',
      dataIndex: 'output_cost',
      key:       'output_cost',
      width:     62,
      align:     'right',
      render: (v) => (
        <span style={{ fontSize: '12px', color: '#1c97d4', fontWeight: 500 }}>
          ${v.toFixed(4)}
        </span>
      ),
    },
    {
      title:     'Total $',
      dataIndex: 'total_cost',
      key:       'total_cost',
      width:     66,
      align:     'right',
      render: (v) => (
        <span style={{ fontSize: '12px', fontWeight: 600 }}>${v.toFixed(4)}</span>
      ),
    },
  ];

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Layout style={{ minHeight: '100vh' }}>

      {/* ── Left sidebar (identical to AgentObservatoryHome) ── */}
      <Sider
        width={60}
        collapsible
        collapsed={true}
        collapsedWidth={60}
        trigger={null}
        className="sidebar-gradient"
        style={{
          display:        'flex',
          background:     'linear-gradient(180deg, #162349 0%, #1c97d4 100%)',
          flexDirection:  'column',
          height:         '100vh',
          position:       'fixed',
          left:           0,
          top:            0,
          bottom:         0,
          zIndex:         10,
        }}
      >
        <div style={{
          display:        'flex',
          flexDirection:  'column',
          justifyContent: 'space-between',
          height:         '100%',
          alignItems:     'center',
          padding:        '20px 0',
        }}>
          <div />
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', alignItems: 'center' }}>
            <div id="containerDiv">
              <hr style={{ backgroundColor: 'white', color: 'white', height: '10px', border: 'none' }} />
            </div>

            <Tooltip title={username} placement="right">
              <Avatar
                style={{ backgroundColor: 'white', color: '#162349', cursor: 'pointer', border: '2px solid white' }}
                icon={<UserOutlined />}
              />
            </Tooltip>

            <Tooltip title="Logout" placement="right">
              <Button
                type="text"
                icon={<LogoutOutlined style={{ color: 'white' }} />}
                onClick={onLogout}
                style={{ color: 'white' }}
              />
            </Tooltip>

            <EnvVersionLabel style={{ color: 'rgba(255,255,255,0.6)', fontSize: '12px', fontWeight: 'Bold' }} />
          </div>
        </div>
      </Sider>

      {/* ── Main layout ── */}
      <Layout style={{ marginLeft: '60px' }}>

        {/* ── Header with tab navigation ── */}
        <Header style={{
          background: '#162349',
          padding:    '8px 24px 0 24px',
          height:     'auto',
          color:      'white',
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-end', gap: '0' }}>
            <TabNavigation
              features={features}
              activeTab="OBSERVATORY"
              tabOrder={localTabOrder}
              onRefresh={handleComputeMetrics}
              refreshing={computing}
            />
          </div>
        </Header>

        {/* ── Content ── */}
        <Content style={{
          padding:    '24px',
          background: 'linear-gradient(to bottom, #f8f9fa 0%, #f0f2f5 33%, #ced4da 90%, #ced4da 100%)',
        }}>

          {/* ── Row 0: Filter panel + Summary strip side by side ── */}
          <Row gutter={[16, 16]} style={{ marginBottom: '16px' }} align="stretch">

            {/* Filter card — simple: just window size + actions */}
            <Col xs={24} lg={8}>
              <Card
                style={{ height: '100%', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                bodyStyle={{ padding: '16px', height: '100%', display: 'flex', alignItems: 'center' }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <div>
                    <Text strong style={{ display: 'block', marginBottom: '4px' }}>Time Window</Text>
                    <Select
                      value={windowDays}
                      onChange={setWindowDays}
                      style={{ width: '100%' }}
                    >
                      {WINDOW_OPTIONS.map(o => (
                        <Option key={o.value} value={o.value}>{o.label}</Option>
                      ))}
                    </Select>
                  </div>

                  <Space size="middle" wrap>
                    <Button
                      type="primary"
                      size="large"
                      onClick={handleApplyFilters}
                      style={{ background: '#92A372', borderColor: '#92A372' }}
                    >
                      Apply Filters
                    </Button>
                    <Button
                      type="default"
                      size="large"
                      icon={<ReloadOutlined />}
                      onClick={handleComputeMetrics}
                      loading={computing}
                    >
                      Compute Metrics
                    </Button>
                  </Space>

                  <Space size="small">
                    <ClockCircleOutlined style={{ color: '#999' }} />
                    <Text type="secondary" style={{ fontSize: '13px' }}>
                      Last computed: {formatLastComputation()}
                    </Text>
                  </Space>
                </Space>
              </Card>
            </Col>

            {/* Summary strip */}
            <Col xs={24} lg={16}>
              <SummaryMetricsCard
                metrics={buildSummaryMetrics(summaryData)}
                loading={summaryLoading}
                error={summaryError}
                periodLabel={
                  summaryData
                    ? `${summaryData.window_start_date} - ${summaryData.window_end_date}`
                    : null
                }
                cardStyle={{ height: '100%', marginBottom: 0 }}
              />
            </Col>
          </Row>

          {/* ── Row 1: Four bar charts ── */}
          <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>

            {/* Bar 1 — Daily in/out tokens */}
            <Col xs={24} sm={12} md={6}>
              {renderChartCard(
                'Daily Token Trend',
                dailyTokensChart ? (
                  <BarChartComponent
                    data={dailyTokensChart}
                    title="Daily Token Trend"
                    stacked={false}
                  />
                ) : null,
                loadingStates.dailyTrend,
                dailyTrendData?.error
              )}
            </Col>

            {/* Bar 2 — Daily in/out cost */}
            <Col xs={24} sm={12} md={6}>
              {renderChartCard(
                'Daily Cost Trend',
                dailyCostChart ? (
                  <BarChartComponent
                    data={dailyCostChart}
                    title="Daily Cost Trend"
                    stacked={false}
                  />
                ) : null,
                loadingStates.dailyTrend,
                dailyTrendData?.error
              )}
            </Col>

            {/* Bar 3 — Batch in/out tokens */}
            <Col xs={24} sm={12} md={6}>
              {renderChartCard(
                'Batch Token Breakdown',
                batchTokensChart ? (
                  <BarChartComponent
                    data={batchTokensChart}
                    title="Batch Token Breakdown"
                    stacked={false}
                  />
                ) : null,
                loadingStates.batch,
                batchData?.error
              )}
            </Col>

            {/* Bar 4 — Batch in/out cost */}
            <Col xs={24} sm={12} md={6}>
              {renderChartCard(
                'Batch Cost Breakdown',
                batchCostChart ? (
                  <BarChartComponent
                    data={batchCostChart}
                    title="Batch Cost Breakdown"
                    stacked={false}
                  />
                ) : null,
                loadingStates.batch,
                batchData?.error
              )}
            </Col>
          </Row>

          {/* ── Row 2: Three pies + Top-20 table ── */}
          <Row gutter={[16, 16]}>

            {/* Pie 1 — LLM model cost share */}
            <Col xs={24} sm={12} md={6}>
              {renderChartCard(
                'LLM Model Cost Share',
                modelData && !modelData.error ? (
                  <PieChartComponent
                    data={modelData}
                    title="LLM Model Cost Share"
                    showLabels={false}
                    colors={PIE_COLORS}
                  />
                ) : null,
                loadingStates.model,
                modelData?.error
              )}
            </Col>

            {/* Pie 2 — Chart type cost share */}
            <Col xs={24} sm={12} md={6}>
              {renderChartCard(
                'Cost by Chart Type',
                chartTypeData && !chartTypeData.error ? (
                  <PieChartComponent
                    data={chartTypeData}
                    title="Cost by Chart Type"
                    showLabels={false}
                    colors={PIE_COLORS}
                  />
                ) : null,
                loadingStates.chartType,
                chartTypeData?.error
              )}
            </Col>

            {/* Pie 3 — Processor cost share */}
            <Col xs={24} sm={12} md={6}>
              {renderChartCard(
                'Cost by Processor',
                processorData && !processorData.error ? (
                  <PieChartComponent
                    data={processorData}
                    title="Cost by Processor"
                    showLabels={false}
                    colors={PIE_COLORS}
                  />
                ) : null,
                loadingStates.processor,
                processorData?.error
              )}
            </Col>

            {/* Top 20 charts by cost */}
            <Col xs={24} sm={12} md={6}>
              <Card
                title={
                  <span style={{ fontSize: '16px', fontWeight: '600' }}>
                    Top 20 Charts by Cost
                  </span>
                }
                style={{ minHeight: '500px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)' }}
                bodyStyle={{ padding: '8px' }}
              >
                {loadingStates.topCharts ? (
                  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px' }}>
                    <Spin size="large" tip="Loading Top 20…" />
                  </div>
                ) : topChartsData?.error ? (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#f5222d', fontSize: '13px' }}>
                    {topChartsData.error}
                  </div>
                ) : topChartsData?.charts?.length ? (
                  <>
                    <div style={{ fontSize: '11px', color: '#8c8c8c', padding: '4px 8px 6px' }}>
                      {topChartsData.start_date} – {topChartsData.end_date}
                    </div>
                    <Table
                      columns={top20Columns}
                      dataSource={topChartsData.charts}
                      rowKey="chart_id"
                      size="small"
                      pagination={false}
                      scroll={{ x: 490, y: 420 }}
                      style={{ fontSize: '12px' }}
                      rowClassName={(_, i) =>
                        i % 2 === 0 ? 'ant-table-row-striped' : ''
                      }
                    />
                  </>
                ) : (
                  <div style={{
                    display:        'flex',
                    justifyContent: 'center',
                    alignItems:     'center',
                    height:         '400px',
                    color:          '#999',
                    flexDirection:  'column',
                    gap:            '8px',
                  }}>
                    <div style={{ fontSize: '16px' }}>No data available</div>
                    <div style={{ fontSize: '13px' }}>Click "Apply Filters" to load</div>
                  </div>
                )}
              </Card>
            </Col>
          </Row>

        </Content>
      </Layout>
    </Layout>
  );
};

export default HCCObservabilityHome;